import { useState, useEffect, useRef } from 'react'
import type { CoverLetterTone, JobApplication } from '@/core/types'
import { coverLetterApi } from '@/core/api/cover-letter.api'
import { useAuth } from '@/core/hooks/useAuth'
import { supabaseConfigured } from '@/core/storage/supabase'
import {
  X, Sparkles, Loader2, Check, RotateCcw, Copy, Save
} from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface Props {
  application: JobApplication
  onClose: () => void
  onSaved: (coverLetterId: string) => void
}

type GenerationStep = 'input' | 'generating' | 'review'

const TONE_OPTIONS: { value: CoverLetterTone; label: string; description: string }[] = [
  { value: 'professional', label: 'Professional', description: 'Formal business tone with emphasis on qualifications' },
  { value: 'conversational', label: 'Conversational', description: 'Warm, approachable tone that shows personality' },
  { value: 'creative', label: 'Creative', description: 'Compelling and memorable with vivid language' },
]

// ============================================================================
// Component
// ============================================================================

export function CoverLetterGenerator({ application, onClose, onSaved }: Props) {
  const { isAuthenticated } = useAuth()
  const [step, setStep] = useState<GenerationStep>('input')
  const [jobDescription, setJobDescription] = useState('')
  const [tone, setTone] = useState<CoverLetterTone>('professional')
  const [editContent, setEditContent] = useState('')
  const [existingLetter, setExistingLetter] = useState<{ id: string; content: string; tone: CoverLetterTone; jobDescription: string; createdAt: Date } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    // Load existing cover letter if one exists
    async function loadExisting() {
      if (application.coverLetterId) {
        const letter = await coverLetterApi.getById(application.coverLetterId)
        if (letter) {
          setExistingLetter(letter)
          setJobDescription(letter.jobDescription)
          setTone(letter.tone)
        }
      }
    }
    loadExisting()
  }, [application.coverLetterId])

  async function handleGenerate() {
    if (!jobDescription.trim()) {
      setError('Please paste the job description to generate a cover letter.')
      return
    }

    setError(null)
    setStep('generating')

    try {
      const result = await coverLetterApi.generate({
        applicationId: application.id,
        jobDescription: jobDescription.trim(),
        tone,
      })
      setEditContent(result.generatedContent)
      setStep('review')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate cover letter')
      setStep('input')
    }
  }

  async function handleSave() {
    setSaving(true)
    try {
      if (existingLetter) {
        await coverLetterApi.updateContent(existingLetter.id, editContent)
        onSaved(existingLetter.id)
      }
      // If we just generated, the API already saved it
      if (step === 'review') {
        const letter = await coverLetterApi.getByApplicationId(application.id)
        if (letter) {
          onSaved(letter.id)
        }
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save cover letter')
    } finally {
      setSaving(false)
    }
  }

  async function handleRegenerate() {
    setStep('input')
  }

  function handleCopy() {
    navigator.clipboard.writeText(editContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleUseExisting() {
    if (existingLetter) {
      setEditContent(existingLetter.content)
      setStep('review')
    }
  }

  const isConfigured = coverLetterApi.isConfigured()
  const needsSignInForSecureAi = supabaseConfigured && !isAuthenticated

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[5vh] bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Cover Letter Generator</h2>
              <p className="text-xs text-gray-500">
                {application.company} — {application.position}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Not configured warning */}
          {needsSignInForSecureAi && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Sign in to use AI.</strong> Cover letters call the{' '}
              <code className="bg-amber-100 px-1 rounded">ai-cover-letter</code> Edge Function so your API key stays on
              the server (<code className="bg-amber-100 px-1 rounded">AI_API_KEY</code> secret), not in the browser.
            </div>
          )}

          {!isConfigured && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>AI not configured.</strong> Add Supabase Edge secrets{' '}
              <code className="bg-amber-100 px-1 rounded">AI_API_KEY</code> (required), optionally{' '}
              <code className="bg-amber-100 px-1 rounded">AI_API_URL</code> and{' '}
              <code className="bg-amber-100 px-1 rounded">AI_MODEL</code>, deploy{' '}
              <code className="bg-amber-100 px-1 rounded">ai-cover-letter</code>, then sign in. Production
              bundles never include an AI provider key; only{' '}
              <code className="bg-amber-100 px-1 rounded">import.meta.env.DEV</code> allows{' '}
              <code className="bg-amber-100 px-1 rounded">VITE_AI_*</code> as an insecure shortcut.
            </div>
          )}

          {/* Step: Input */}
          {step === 'input' && (
            <div className="space-y-5">
              {/* Existing letter notice */}
              {existingLetter && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Existing cover letter found</p>
                    <p className="text-xs text-blue-600">
                      Generated on {new Date(existingLetter.createdAt).toLocaleDateString()} with {existingLetter.tone} tone
                    </p>
                  </div>
                  <button
                    onClick={handleUseExisting}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                  >
                    View & Edit
                  </button>
                </div>
              )}

              {/* Job Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Job Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={textareaRef}
                  value={jobDescription}
                  onChange={e => setJobDescription(e.target.value)}
                  placeholder="Paste the job description here. The more detail you provide, the better the cover letter will be..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-40 resize-vertical placeholder:text-gray-400"
                />
              </div>

              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                <div className="grid grid-cols-3 gap-2">
                  {TONE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTone(opt.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        tone === opt.value
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className={`text-sm font-semibold ${tone === opt.value ? 'text-blue-700' : 'text-gray-800'}`}>
                        {opt.label}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5 leading-tight">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={!isConfigured || !jobDescription.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                <Sparkles className="w-4 h-4" />
                Generate Cover Letter
              </button>
            </div>
          )}

          {/* Step: Generating */}
          {step === 'generating' && (
            <div className="py-12 flex flex-col items-center gap-4">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent" />
                <Sparkles className="w-5 h-5 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">Generating your cover letter...</p>
                <p className="text-xs text-gray-500 mt-1">Analyzing job description and your profile with {tone} tone</p>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span>Generated with <strong>{tone}</strong> tone</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Regenerate
                  </button>
                </div>
              </div>

              {/* Editable Cover Letter */}
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-72 resize-vertical"
              />

              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  {editContent.split(/\s+/).length} words
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors font-medium"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save Cover Letter
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
